import React, { useState } from 'react';
import { AnimatePresence, motion, type MotionProps } from 'framer-motion';
import { WelcomeStep } from './steps/welcome-step';
import { AvatarStep } from './steps/avatar-step';
import { BirthdayStep } from './steps/birthday-step';
import { QuestionStep } from './steps/question-step';
import { CompletionStep, OnboardingData } from './steps/completion-step';
import { OnboardingProgress } from './onboarding-progress';

const QUESTIONS = [
  {
    id: 'professionalDescription' as keyof OnboardingData,
    question: 'How would you describe yourself professionally in a few words?',
    placeholder: 'e.g., Creative problem-solver, detail-oriented leader...',
    subtitle: "Let's start with how you see yourself at work",
  },
  {
    id: 'professionalHabit' as keyof OnboardingData,
    question: "What's one professional habit or mindset that you're proud of?",
    placeholder: 'e.g., I always follow through on commitments...',
    subtitle: 'Share something that defines your work ethic',
  },
  {
    id: 'friendGroupKnownFor' as keyof OnboardingData,
    question: 'What are you known for in your friend group?',
    placeholder: 'e.g., The one who plans all the trips...',
    subtitle: "Now let's get to know the real you",
  },
  {
    id: 'hobbies' as keyof OnboardingData,
    question: 'What hobbies are you passionate about?',
    placeholder: 'e.g., Photography, hiking, cooking new recipes...',
    subtitle: 'What instantly puts you in a good mood?',
  },
  {
    id: 'lifeGoal' as keyof OnboardingData,
    question: 'What do you want to achieve in life?',
    placeholder: 'e.g., Build something meaningful, travel to 50 countries...',
    subtitle: 'Work or personal - what makes you proud?',
  },
  {
    id: 'funFact' as keyof OnboardingData,
    question: "What's one fun, random fact about you?",
    placeholder: 'e.g., I can solve a Rubik\'s cube in under 2 minutes...',
    subtitle: 'What makes you uniquely you?',
  },
  {
    id: 'careerLearning' as keyof OnboardingData,
    question: 'What would you like to learn more about in the coming year?',
    placeholder: 'e.g., Leadership skills, a new programming language...',
    subtitle: "Career-focused growth you're excited about",
  },
];

interface OnboardingFlowProps {
  onComplete: (data: OnboardingData) => Promise<void>;
  onSkip: () => void;
  initialData?: Partial<OnboardingData>;
  existingPhotoURL?: string;
}

export function OnboardingFlow({ onComplete, onSkip, initialData, existingPhotoURL }: OnboardingFlowProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [previewUrl, setPreviewUrl] = useState<string | null>(existingPhotoURL || null);
  const [data, setData] = useState<OnboardingData>({
    avatar: null,
    birthday: initialData?.birthday || null,
    professionalDescription: initialData?.professionalDescription || '',
    professionalHabit: initialData?.professionalHabit || '',
    friendGroupKnownFor: initialData?.friendGroupKnownFor || '',
    hobbies: initialData?.hobbies || '',
    lifeGoal: initialData?.lifeGoal || '',
    funFact: initialData?.funFact || '',
    careerLearning: initialData?.careerLearning || '',
  });

  const totalSteps = 3 + QUESTIONS.length + 1; // welcome + avatar + birthday + questions + completion
  const progress = (currentStep / (totalSteps - 1)) * 100;

  const goNext = () => {
    setDirection(1);
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const goBack = () => {
    setDirection(-1);
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const updateData = (key: keyof OnboardingData, value: string | Date | File | null) => {
    setData((prev) => ({ ...prev, [key]: value }));
    
    // Handle avatar preview
    if (key === 'avatar' && value instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(value);
    }
  };

  const handleComplete = async () => {
    await onComplete(data);
  };

  const renderStep = () => {
    if (currentStep === 0) {
      return <WelcomeStep onNext={goNext} />;
    }

    if (currentStep === 1) {
      return (
        <AvatarStep
          avatar={data.avatar}
          onAvatarChange={(avatar) => updateData('avatar', avatar)}
          onNext={goNext}
          onBack={goBack}
          existingPhotoURL={existingPhotoURL}
        />
      );
    }

    if (currentStep === 2) {
      return (
        <BirthdayStep
          birthday={data.birthday}
          onBirthdayChange={(date) => updateData('birthday', date)}
          onNext={goNext}
          onBack={goBack}
        />
      );
    }

    const questionIndex = currentStep - 3;
    if (questionIndex < QUESTIONS.length) {
      const question = QUESTIONS[questionIndex];
      return (
        <QuestionStep
          key={question.id}
          question={question.question}
          subtitle={question.subtitle}
          placeholder={question.placeholder}
          value={data[question.id] as string}
          onChange={(value) => updateData(question.id, value)}
          onNext={goNext}
          onBack={goBack}
          questionNumber={questionIndex + 1}
          totalQuestions={QUESTIONS.length}
        />
      );
    }

    return (
      <CompletionStep
        data={data}
        previewUrl={previewUrl}
        onComplete={handleComplete}
        onBack={goBack}
      />
    );
  };

  return (
    <div className="relative min-h-screen flex flex-col" data-testid="onboarding-flow">
      {currentStep > 0 && currentStep < totalSteps - 1 && (
        <OnboardingProgress progress={progress} currentStep={currentStep} totalSteps={totalSteps - 1} />
      )}

      <div className="flex-1 flex items-center justify-center px-4 py-12" data-testid="onboarding-content">
        {/* @ts-ignore - AnimatePresence has TypeScript issues with React 19 */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            custom={direction}
            initial={{ opacity: 0, x: direction * 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -50 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="w-full max-w-2xl"
            data-testid={`onboarding-step-${currentStep}`}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {currentStep < totalSteps - 1 && (
        <div className="pb-8 text-center">
          <button
            onClick={onSkip}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline"
            data-testid="onboarding-button-skip"
          >
            Skip onboarding for now
          </button>
        </div>
      )}
    </div>
  );
}
